import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  LogOut, RefreshCw, Mail, Phone, Building2, FileText, Send, Plus, X,
  Download, Clock, CheckCircle2, Sparkles, Settings, Bot, FileSignature, Upload,
} from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import CFLogo from "@/components/CFLogo";
import AgentChatInbox from "@/components/AgentChatInbox";
import RiskSnapshot from "@/components/RiskSnapshot";
import AIAnalysisCard from "@/components/AIAnalysisCard";
import CourtsCheckButton from "@/components/CourtsCheckButton";
import { AUTH } from "@/constants/testIds";

const STATUS_OPTIONS = [
  "pending", "under_review", "documents_needed", "approved", "funded", "declined",
];

const STATUS_LABEL = {
  pending: "Pending",
  under_review: "Under review",
  documents_needed: "Docs needed",
  approved: "Approved",
  funded: "Funded",
  declined: "Declined",
};

const STATUS_TINT = {
  pending: "text-cf-gold-light",
  under_review: "text-sky-300",
  documents_needed: "text-rose-300",
  approved: "text-emerald-300",
  funded: "text-emerald-400",
  declined: "text-rose-400",
};

const fmt = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

// Dark agent portal palette — uses old cf-* legacy classes from index.css
const DARK_BG = { background: "#05070A", color: "#F8F9FA" };

const AgentDashboard = () => {
  const { user, logout, api } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [newDoc, setNewDoc] = useState("");
  const [refreshingSnap, setRefreshingSnap] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [followupDocs, setFollowupDocs] = useState([]);
  const [followupDraft, setFollowupDraft] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [sendingFollowup, setSendingFollowup] = useState(false);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
      setActiveId((curr) => curr ?? (data.length ? data[0].id : null));
    } catch (err) {
      console.error("Failed to load leads:", err);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const loadActive = useCallback(async (id) => {
    if (!id) { setActive(null); setMessages([]); return; }
    try {
      const [leadRes, msgRes] = await Promise.all([
        api.get(`/leads/${id}`),
        api.get(`/leads/${id}/messages`),
      ]);
      setActive(leadRes.data);
      setMessages(msgRes.data);
    } catch (err) {
      console.error(err);
    }
  }, [api]);

  useEffect(() => { loadLeads(); }, [loadLeads]);
  useEffect(() => { loadActive(activeId); }, [activeId, loadActive]);

  // When active lead changes, reset / prefill the AI follow-up panel.
  useEffect(() => {
    if (!active) { setFollowupDocs([]); setFollowupDraft(""); return; }
    const fromAi = active.ai_analysis?.missing_documents || [];
    setFollowupDocs(fromAi);
    setFollowupDraft("");
  }, [activeId, active?.ai_analysis]);

  if (user === null) {
    return <div style={{ ...DARK_BG, minHeight: "100vh" }} className="flex items-center justify-center">Authenticating…</div>;
  }
  if (user === false) return <Navigate to="/agent/login" replace />;
  if (user.role !== "agent") return <Navigate to="/merchant/dashboard" replace />;

  const updateStatus = async (status) => {
    try {
      await api.patch(`/leads/${active.id}/status`, { status });
      setActive((p) => ({ ...p, status }));
      setLeads((prev) => prev.map((l) => (l.id === active.id ? { ...l, status } : l)));
    } catch (err) { console.error(err); }
  };

  const addRequiredDoc = async () => {
    const name = newDoc.trim();
    if (!name) return;
    const docs = [...(active.required_documents || []).map((d) => d.name), name];
    try {
      const { data } = await api.post(`/leads/${active.id}/required-documents`, { documents: docs });
      setActive((p) => ({ ...p, required_documents: data.required_documents, status: "documents_needed" }));
      setLeads((prev) => prev.map((l) => (l.id === active.id ? { ...l, status: "documents_needed" } : l)));
      setNewDoc("");
    } catch (err) { console.error(err); }
  };

  const removeDoc = async (id) => {
    const docs = (active.required_documents || []).filter((d) => d.id !== id).map((d) => d.name);
    try {
      const { data } = await api.post(`/leads/${active.id}/required-documents`, { documents: docs });
      setActive((p) => ({ ...p, required_documents: data.required_documents }));
    } catch (err) { console.error(err); }
  };

  const sendMsg = async (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || !active) return;
    setDraft("");
    try {
      const { data } = await api.post(`/leads/${active.id}/messages`, { body });
      setMessages((prev) => [...prev, data]);
    } catch (err) { console.error(err); }
  };

  const refreshSnap = async () => {
    if (!active) return;
    setRefreshingSnap(true);
    try {
      const { data } = await api.post(`/leads/${active.id}/risk-snapshot`);
      setActive((p) => ({ ...p, risk_snapshot: data }));
    } catch (err) { console.error(err); }
    finally { setRefreshingSnap(false); }
  };

  const runAiAnalysis = async () => {
    if (!active) return;
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/leads/${active.id}/analyze-statements`, {}, { timeout: 180000 });
      setActive((p) => ({ ...p, ai_analysis: data }));
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      setActive((p) => ({ ...p, ai_analysis: { error: detail } }));
    } finally { setAnalyzing(false); }
  };

  // --------- AI follow-up message draft + send ---------
  const STANDARD_FOLLOWUP_DOCS = [
    "Month-to-date bank statement",
    "Accounts receivable aging report",
    "Voided check",
    "Driver's license (color copy)",
    "Most recent business tax return",
    "YTD profit & loss statement",
  ];

  const toggleFollowupDoc = (name) => {
    setFollowupDocs((prev) => prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]);
  };

  const draftFollowup = async () => {
    if (!active) return;
    setDrafting(true);
    try {
      const { data } = await api.post(`/leads/${active.id}/draft-followup`, {
        documents: followupDocs,
        use_analysis: true,
      }, { timeout: 60000 });
      setFollowupDraft(data.message || "");
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not draft message");
    } finally { setDrafting(false); }
  };

  const sendFollowup = async () => {
    if (!active || !followupDraft.trim()) return;
    setSendingFollowup(true);
    try {
      const { data } = await api.post(`/leads/${active.id}/send-followup`, {
        message: followupDraft.trim(),
        documents: followupDocs,
        set_status_docs_needed: followupDocs.length > 0,
      });
      // Refresh lead + messages
      await loadActive(active.id);
      setLeads((prev) => prev.map((l) => (l.id === active.id && data.status_updated ? { ...l, status: "documents_needed" } : l)));
      setFollowupDraft("");
      setFollowupDocs([]);
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not send follow-up");
    } finally { setSendingFollowup(false); }
  };

  // --------- Contract status ---------
  const updateContractStatus = async (cs) => {
    if (!active) return;
    try {
      await api.patch(`/leads/${active.id}/contract-status`, { contract_status: cs });
      setActive((p) => ({ ...p, contract_status: cs }));
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not update contract status");
    }
  };

  return (
    <div data-testid={AUTH.dashboard} style={{ minHeight: "100vh", background: "#05070A", color: "#F8F9FA" }}>
      <header className="border-b border-white/5 sticky top-0 z-30" style={{ background: "rgba(10,14,23,0.7)", backdropFilter: "blur(16px)" }}>
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <CFLogo variant="paper" testId="agent-header-logo" />
            <div className="hidden md:flex items-center gap-2 ml-4 pl-6 border-l border-white/10">
              <span className="text-[0.7rem] uppercase tracking-[0.24em] text-cf-gold font-semibold">Agent Console</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-xs text-cf-muted">
              {user.email}
            </span>
            <button onClick={loadLeads} className="cf-btn-ghost" style={{ fontSize: "0.7rem", padding: "0.5rem 1rem" }}>
              <RefreshCw size={13} strokeWidth={1.5} /> Refresh
            </button>
            <button
              data-testid={AUTH.logoutBtn}
              onClick={logout}
              className="cf-btn-ghost"
              style={{ fontSize: "0.7rem", padding: "0.5rem 1rem" }}
            >
              <LogOut size={13} strokeWidth={1.5} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-8">
        {/* Status tiles */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-white/5 border border-white/5 mb-8">
          {STATUS_OPTIONS.map((s) => {
            const count = leads.filter((l) => l.status === s).length;
            return (
              <div key={s} className="p-4" style={{ background: "#05070A" }}>
                <div className="text-[0.6rem] uppercase tracking-[0.22em] text-cf-muted">{STATUS_LABEL[s]}</div>
                <div className={`font-serif text-2xl mt-1 ${STATUS_TINT[s]}`}>{count}</div>
              </div>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Leads list */}
          <div className="lg:col-span-5">
            <h2 className="font-serif text-2xl tracking-tight mb-4">Applications</h2>
            <div data-testid={AUTH.leadsTable} className="border border-white/5 max-h-[80vh] overflow-y-auto">
              <div className="grid grid-cols-12 px-4 py-3 text-[0.6rem] uppercase tracking-[0.22em] text-cf-muted sticky top-0" style={{ background: "rgba(10,14,23,0.95)" }}>
                <div className="col-span-6">Business</div>
                <div className="col-span-3">Amount</div>
                <div className="col-span-3 text-right">Status</div>
              </div>
              {loading && <div className="px-4 py-6 text-cf-muted text-sm">Loading…</div>}
              {!loading && leads.length === 0 && (
                <div className="px-4 py-10 text-center text-cf-muted text-sm">No applications yet.</div>
              )}
              {leads.map((l) => (
                <button
                  key={l.id}
                  data-testid={AUTH.leadRow}
                  onClick={() => setActiveId(l.id)}
                  className={`w-full grid grid-cols-12 px-4 py-3 text-sm text-left border-t border-white/5 transition ${activeId === l.id ? "bg-white/[0.04]" : "hover:bg-white/[0.02]"}`}
                >
                  <div className="col-span-6">
                    <div className="text-cf-text">{l.business_name}</div>
                    <div className="text-cf-muted text-xs mt-1 truncate">{l.product} · {fmt(l.created_at)}</div>
                  </div>
                  <div className="col-span-3">{l.funding_amount}</div>
                  <div className={`col-span-3 text-right text-[0.7rem] uppercase tracking-[0.16em] ${STATUS_TINT[l.status] || "text-cf-muted"}`}>
                    {STATUS_LABEL[l.status] || l.status}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Detail panel */}
          <div className="lg:col-span-7">
            {!active && <div className="text-cf-muted text-sm">Select an application to see details.</div>}
            {active && (
              <div className="space-y-6">
                {/* Header */}
                <div className="border border-white/5 p-6" style={{ background: "rgba(10,14,23,0.4)" }}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="text-[0.65rem] uppercase tracking-[0.22em] text-cf-gold font-semibold">Application</div>
                      <h3 className="font-serif text-2xl md:text-3xl tracking-tight mt-1">{active.business_name}</h3>
                      <div className="text-cf-muted text-sm mt-1">{active.contact_name} · {fmt(active.created_at)}</div>
                    </div>
                    <div className={`text-xs uppercase tracking-[0.18em] ${STATUS_TINT[active.status]}`}>{STATUS_LABEL[active.status]}</div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6 text-sm">
                    <Detail icon={Mail} label="Email" value={active.email} />
                    <Detail icon={Phone} label="Phone" value={active.phone} />
                    <Detail icon={Building2} label="Program" value={active.product} />
                    <Detail label="Amount" value={active.funding_amount} />
                    <Detail label="Monthly Rev." value={active.monthly_revenue || "—"} />
                    <Detail label="Time in Biz" value={active.time_in_business || "—"} />
                  </div>

                  {active.notes && (
                    <div className="mt-6 pt-5 border-t border-white/5">
                      <div className="text-[0.65rem] uppercase tracking-[0.22em] text-cf-gold mb-2 font-semibold">Notes</div>
                      <p className="text-cf-muted text-sm leading-relaxed">{active.notes}</p>
                    </div>
                  )}

                  {/* Status workflow */}
                  <div className="mt-6 pt-5 border-t border-white/5">
                    <div className="text-[0.65rem] uppercase tracking-[0.22em] text-cf-gold mb-3 font-semibold">Set status</div>
                    <div className="flex flex-wrap gap-2">
                      {STATUS_OPTIONS.map((s) => (
                        <button
                          key={s}
                          onClick={() => updateStatus(s)}
                          className={`px-3 py-2 text-[0.7rem] uppercase tracking-[0.16em] border transition rounded-full ${
                            active.status === s
                              ? "bg-cf-gold-light text-cf-ink border-cf-gold-light"
                              : "border-white/10 text-cf-muted hover:border-cf-gold-light hover:text-cf-gold-light"
                          }`}
                        >
                          {STATUS_LABEL[s]}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Risk Snapshot (deterministic, no AI) */}
                <RiskSnapshot snap={active.risk_snapshot} onRefresh={refreshSnap} refreshing={refreshingSnap} />

                {/* AI bank-statement analysis */}
                <AIAnalysisCard
                  analysis={active.ai_analysis}
                  onAnalyze={runAiAnalysis}
                  analyzing={analyzing}
                  hasDocs={(active.required_documents || []).some((d) => d.status === "submitted")}
                />

                {/* NY Courts (NYSCEF) external check */}
                <CourtsCheckButton lead={active} api={api} onSaved={() => loadActive(active.id)} />

                {/* Contract status — visible once approved */}
                {active.status === "approved" && (
                  <div data-testid="contract-status-panel" className="border border-white/5 rounded-lg p-5" style={{ background: "rgba(10,14,23,0.4)" }}>
                    <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                      <div>
                        <div className="text-[0.65rem] uppercase tracking-[0.22em] text-cf-gold font-semibold flex items-center gap-1">
                          <FileSignature size={11} /> Contract
                        </div>
                        <p className="text-cf-muted text-xs mt-1">
                          Merchant sees this in their portal.
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { k: "not_sent", label: "Not sent" },
                        { k: "out_for_signature", label: "Out for signature" },
                        { k: "signed", label: "Signed" },
                      ].map((opt) => (
                        <button
                          key={opt.k}
                          data-testid={`contract-status-${opt.k}`}
                          onClick={() => updateContractStatus(opt.k)}
                          className={`px-3 py-2 text-[0.7rem] uppercase tracking-[0.16em] border transition rounded-full ${
                            active.contract_status === opt.k
                              ? "bg-cf-gold-light text-cf-ink border-cf-gold-light"
                              : "border-white/10 text-cf-muted hover:border-cf-gold-light hover:text-cf-gold-light"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI follow-up: ask merchant for more docs */}
                <div data-testid="ai-followup-panel" className="border border-white/5 rounded-lg p-5" style={{ background: "rgba(10,14,23,0.4)" }}>
                  <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                    <div>
                      <div className="text-[0.65rem] uppercase tracking-[0.22em] text-cf-gold font-semibold flex items-center gap-1">
                        <Bot size={11} /> AI follow-up to merchant
                      </div>
                      <p className="text-cf-muted text-xs mt-1">
                        Pick what&apos;s missing, let AI draft a friendly message, review &amp; send. Selected items are added to the merchant&apos;s upload list.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-3">
                    {STANDARD_FOLLOWUP_DOCS.map((name) => {
                      const on = followupDocs.includes(name);
                      return (
                        <button
                          key={name}
                          data-testid={`followup-doc-toggle-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          onClick={() => toggleFollowupDoc(name)}
                          className={`px-3 py-1.5 text-[0.7rem] border transition rounded-full ${
                            on
                              ? "bg-cf-gold-light text-cf-ink border-cf-gold-light"
                              : "border-white/10 text-cf-muted hover:border-cf-gold-light hover:text-cf-gold-light"
                          }`}
                        >
                          {on ? "✓ " : ""}{name}
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <button
                      data-testid="draft-ai-followup"
                      onClick={draftFollowup}
                      disabled={drafting || followupDocs.length === 0}
                      className="cf-btn-ghost"
                      style={{ fontSize: "0.7rem", padding: "0.5rem 1rem" }}
                    >
                      <Sparkles size={12} /> {drafting ? "Drafting…" : "Draft AI message"}
                    </button>
                    {followupDocs.length === 0 && (
                      <span className="text-[0.65rem] text-cf-muted">Pick at least one item above.</span>
                    )}
                  </div>

                  <textarea
                    data-testid="followup-message"
                    value={followupDraft}
                    onChange={(e) => setFollowupDraft(e.target.value)}
                    placeholder="The AI draft will appear here — feel free to edit before sending."
                    rows={6}
                    className="cf-input w-full resize-none"
                  />

                  <div className="flex justify-end mt-3">
                    <button
                      data-testid="send-ai-followup"
                      onClick={sendFollowup}
                      disabled={sendingFollowup || !followupDraft.trim()}
                      className="cf-btn-ghost"
                      style={{ fontSize: "0.7rem", padding: "0.5rem 1rem", background: "#B68A35", color: "#fff", borderColor: "#B68A35" }}
                    >
                      <Send size={12} /> {sendingFollowup ? "Sending…" : "Send to merchant"}
                    </button>
                  </div>
                </div>

                {/* Required documents editor */}
                <div className="border border-white/5 p-6" style={{ background: "rgba(10,14,23,0.4)" }}>
                  <div className="flex items-baseline justify-between mb-4">
                    <div>
                      <div className="text-[0.65rem] uppercase tracking-[0.22em] text-cf-gold font-semibold">Required documents</div>
                      <div className="text-cf-muted text-xs mt-1">Adding any doc auto-sets status to <em>Documents needed</em>.</div>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    {(active.required_documents || []).length === 0 && (
                      <div className="text-cf-muted text-sm py-3">No documents requested yet.</div>
                    )}
                    {(active.required_documents || []).map((d) => (
                      <div key={d.id} className="flex items-center gap-3 p-3 border border-white/5 rounded-lg">
                        <FileText size={15} className="text-cf-gold-light shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm">{d.name}</div>
                          {d.status === "submitted" ? (
                            <div className="text-xs text-emerald-300 mt-0.5 flex items-center gap-1">
                              <CheckCircle2 size={11} /> {d.file_name}
                            </div>
                          ) : (
                            <div className="text-xs text-cf-muted mt-0.5 flex items-center gap-1">
                              <Clock size={11} /> Awaiting upload
                            </div>
                          )}
                        </div>
                        {d.status === "submitted" && d.file_id && (
                          <a
                            href={`${api.defaults.baseURL}/documents/${d.file_id}/download`}
                            target="_blank" rel="noreferrer"
                            className="text-cf-gold-light hover:text-cf-gold text-xs inline-flex items-center gap-1"
                          >
                            <Download size={12} /> Download
                          </a>
                        )}
                        <button onClick={() => removeDoc(d.id)} className="text-cf-muted hover:text-rose-400 transition" title="Remove">
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      value={newDoc}
                      onChange={(e) => setNewDoc(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addRequiredDoc())}
                      placeholder="e.g., Last 3 months bank statements"
                      className="cf-input flex-1"
                    />
                    <button onClick={addRequiredDoc} className="cf-btn-ghost" style={{ fontSize: "0.7rem", padding: "0.6rem 1rem" }}>
                      <Plus size={13} /> Request
                    </button>
                  </div>
                </div>

                {/* Chat */}
                <div className="border border-white/5 p-6" style={{ background: "rgba(10,14,23,0.4)" }}>
                  <div className="text-[0.65rem] uppercase tracking-[0.22em] text-cf-gold font-semibold mb-4">Messages with merchant</div>
                  <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1 mb-4">
                    {messages.length === 0 && (
                      <div className="text-cf-muted text-sm py-4 text-center">No messages yet.</div>
                    )}
                    {messages.map((m) => {
                      const mine = m.from_role === "agent";
                      return (
                        <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${mine ? "bg-cf-gold-light text-cf-ink" : "bg-white/5 text-cf-text"}`}>
                            <div className={`text-[0.6rem] uppercase tracking-[0.16em] mb-1 ${mine ? "text-cf-ink/70" : "text-cf-muted"}`}>
                              {mine ? "You" : m.from_name} · {fmt(m.created_at)}
                            </div>
                            <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.body}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <form onSubmit={sendMsg} className="flex items-center gap-2">
                    <input
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      placeholder="Type a message to the merchant…"
                      className="cf-input flex-1"
                    />
                    <button type="submit" disabled={!draft.trim()} className="cf-btn-ghost" style={{ fontSize: "0.7rem", padding: "0.6rem 1rem" }}>
                      <Send size={13} /> Send
                    </button>
                  </form>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-12 flex justify-between items-center text-xs text-cf-muted">
          <Link to="/" className="hover:text-cf-gold-light transition">← View public site</Link>
          <span>Capless Funding · Direct Lender</span>
        </div>
      </div>

      <AgentChatInbox />
    </div>
  );
};

const Detail = ({ icon: Icon, label, value }) => (
  <div>
    <div className="flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.2em] text-cf-muted mb-1">
      {Icon && <Icon size={11} strokeWidth={1.5} className="text-cf-gold-light" />} {label}
    </div>
    <div className="text-cf-text break-words text-sm">{value}</div>
  </div>
);

export default AgentDashboard;
